import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loading from "@/common/components/ui/loading/Loading";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { getCollisionDetails } from "@/common/services/collision.api";
import { Collision } from "@/types/collision.type";
import { IVehicleVisualRecord } from "@/types/vehicle-visual-record.type";
import { IMaintenanceVisualRecord } from "@/types/maintenance-visual-record.type";
import ReturnedDetails from "../vehicles/components/vehicle-visual-records/vehicle-devolution/CardDevolutionDetails/ReturnedDetails";
import VehicleEndMaintenanceDetails from "../vehicles/components/vehicle-visual-records/vehicle-end-maintenance/vehicle-end-maintenance/VehicleEndMaintenanceDetails";
import Dashboard from "@/common/components/layout/Dashboard";
import { getStatusLabelsVehicle } from "@/modules/vehicles/components/TableVehicles";
import { VEHICLE_STATUS } from "@/types/vehicle.type";

type CollisionDetailsResponse = {
  collision: Collision;
  vehicle?: {
    status: VEHICLE_STATUS;
    _id: string;
    licensePlate: string;
  } | null;
  vehicleVisualRecord?: IVehicleVisualRecord | null;
  maintenanceVisualRecord?: IMaintenanceVisualRecord | null;
};

const FILES_BASE_URL =
  import.meta.env.VITE_ASSETS_URL || import.meta.env.VITE_API_URL;

type PhotoFile = {
  content?: string;
  url?: string;
  src?: string;
};

// função para normalizar URLs de fotos, garantindo que sejam URLs completas
function normalizePhotoUrl(raw?: string) {
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${FILES_BASE_URL}${path}`;
}

function getPhotoUrl(photo: unknown): string {
  const file = photo as PhotoFile;
  return file?.content || file?.url || file?.src || "";
}
// função para extrair URLs de fotos de registros visuais de devolução
function getDevolutionPhotos(vehicleVisualRecord: unknown): string[] {
  const vr = vehicleVisualRecord as {
    photos?: {
      devolutionPhotos?: unknown;
      returnPhotos?: unknown;
    };
    devolutionPhotos?: unknown;
  };

  const list =
    vr?.photos?.devolutionPhotos ||
    vr?.photos?.returnPhotos ||
    vr?.devolutionPhotos ||
    [];

  return (Array.isArray(list) ? list : [])
    .map(getPhotoUrl)
    .filter(Boolean)
    .map((u: string) => normalizePhotoUrl(u))
    .filter(Boolean);
}
// função para extrair URLs de fotos de registros visuais de manutenção
function getEndMaintenancePhotos(maintenanceVisualRecord: unknown): string[] {
  const mvr = maintenanceVisualRecord as {
    photosMaintenance?: Array<{ endMaintenance?: unknown }>;
    photos?: { endMaintenance?: unknown };
    endMaintenance?: unknown;
  };

  const list =
    mvr?.photosMaintenance?.[0]?.endMaintenance ||
    mvr?.photos?.endMaintenance ||
    mvr?.endMaintenance ||
    [];

  return (Array.isArray(list) ? list : [])
    .map(getPhotoUrl)
    .filter(Boolean)
    .map((u: string) => normalizePhotoUrl(u))
    .filter(Boolean);
}

function EvidenceEmptyState() {
  return (
    <p className="text-sm text-gray-600">
      Sem evidências vinculadas para esta colisão.
    </p>
  );
}
// Componente principal para exibir detalhes de uma colisão, incluindo informações do veículo, peças danificadas e evidências fotográficas
export default function CollisionDetails() {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<CollisionDetailsResponse | null>(null);

  const [showReturnedDetails, setShowReturnedDetails] = useState(false);
  const [showMaintenanceDetails, setShowMaintenanceDetails] = useState(false);

  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!vehicleId) return;

      setIsLoading(true);
      try {
        const result = await getCollisionDetails(vehicleId);

        if (!result?.ok) {
          notifyError(result?.message || "Erro ao buscar detalhes da colisão");
          setData(null);
          return;
        }

        setData(result.data as CollisionDetailsResponse);
      } catch (err) {
        console.error(err);
        notifyError("Erro ao buscar detalhes da colisão");
        setData(null);
      } finally {
        setIsLoading(false);
      }
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const collision = data?.collision;
  const source = collision?.source;
  const returnReason =
    typeof (data?.vehicleVisualRecord as { returnReason?: unknown } | undefined)?.returnReason ===
      "string"
      ? ((data?.vehicleVisualRecord as { returnReason?: string }).returnReason as string)
      : "";
  const hasLinkedDevolutionEvidence =
    source === "devolution" && Boolean(collision?.vehicleVisualRecordId);
  const hasLinkedMaintenanceEvidence =
    source === "maintenance" && Boolean(collision?.maintenanceVisualRecordId);

  const devolutionPhotos = useMemo(
    () => getDevolutionPhotos(data?.vehicleVisualRecord) || [],
    [data?.vehicleVisualRecord]
  );

  const endMaintenancePhotos = useMemo(
    () => getEndMaintenancePhotos(data?.maintenanceVisualRecord) || [],
    [data?.maintenanceVisualRecord]
  );

  if (isLoading) return <Loading />;

  if (!collision) {
    return (
      <Dashboard>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900">
              Detalhes da Colisão
            </h1>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-redPrimary text-white px-4 py-2 rounded shadow"
            >
              Voltar
            </button>
          </div>

          <div className="bg-white border rounded p-4">
            <p className="text-gray-700">Nenhum detalhe encontrado.</p>
          </div>
        </div>
      </Dashboard>
    );
  }

  const piecesCount = Array.isArray(collision.pieces) ? collision.pieces.length : 0;

  return (
    <Dashboard>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Detalhes da Colisão
            </h1>
            <p className="text-sm text-gray-600">
              Placa:{" "}
              <span className="text-gray-900 font-medium">
                {collision.licensePlate}
              </span>
            </p>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-redPrimary text-white px-4 py-2 rounded shadow"
            >
              Voltar
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-white border rounded p-4">
            <p className="text-xs text-gray-500">Parceiro</p>
            <p className="text-gray-900 font-medium">
              {collision.partnerName || "-"}
            </p>
          </div>

          <div className="bg-white border rounded p-4">
            <p className="text-xs text-gray-500">Status do veículo</p>
            <p className="text-gray-900 font-medium">
              {data?.vehicle?.status ? getStatusLabelsVehicle(data.vehicle.status) : "-"}
            </p>
          </div>

          <div className="bg-white border rounded p-4">
            <p className="text-xs text-gray-500">Valor do dano</p>
            <p className="text-gray-900 font-medium">
              R${" "}
              {Number(collision.value || 0).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
        </div>

        {/* Peças */}
        <div className="bg-white border rounded p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-gray-900">Peças</h2>
            <span className="text-sm text-gray-600">{piecesCount} item(s)</span>
          </div>

          {Array.isArray(collision.pieces) && collision.pieces.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1">
              {collision.pieces.map((p, idx) => (
                <li key={`${p}-${idx}`} className="text-sm text-gray-800">
                  {p}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600">Sem peças registradas.</p>
          )}
        </div>

        {/* Evidências */}
        <div className="bg-white border rounded p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            Evidências (fotos e observações)
          </h2>

          {source === "devolution" && (
            <>
              {hasLinkedDevolutionEvidence && devolutionPhotos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {devolutionPhotos.map((src, i) => (
                    <button
                      key={`${src}-${i}`}
                      type="button"
                      onClick={() => setSelectedPhoto(src)}
                      className="block aspect-square overflow-hidden rounded border bg-gray-50"
                      title="Abrir foto"
                    >
                      <img
                        src={src}
                        alt={`devolucao-${i + 1}`}
                        className="h-full w-full object-cover hover:scale-[1.02] transition-transform"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <EvidenceEmptyState />
              )}

              {hasLinkedDevolutionEvidence && returnReason && (
                <p className="text-sm text-gray-800 mt-3 whitespace-pre-wrap">
                  {returnReason}
                </p>
              )}

              {hasLinkedDevolutionEvidence && data?.vehicleVisualRecord && (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setShowReturnedDetails(true)}
                    className="px-4 py-2 rounded shadow text-white bg-redPrimary"
                  >
                    Ver detalhes completos da devolução
                  </button>
                </div>
              )}
            </>
          )}

          {source === "maintenance" && (
            <>
              {hasLinkedMaintenanceEvidence && endMaintenancePhotos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {endMaintenancePhotos.map((src, i) => (
                    <button
                      key={`${src}-${i}`}
                      type="button"
                      onClick={() => setSelectedPhoto(src)}
                      className="block aspect-square overflow-hidden rounded border bg-gray-50"
                      title="Abrir foto"
                    >
                      <img
                        src={src}
                        alt={`manutencao-${i + 1}`}
                        className="h-full w-full object-cover hover:scale-[1.02] transition-transform"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <EvidenceEmptyState />
              )}

              {hasLinkedMaintenanceEvidence && data?.maintenanceVisualRecord && (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setShowMaintenanceDetails(true)}
                    className="px-4 py-2 rounded shadow text-white bg-redPrimary"
                  >
                    Ver detalhes completos da manutenção
                  </button>
                </div>
              )}
            </>
          )}
          {!source && <EvidenceEmptyState />}
        </div>

        { }
        {showReturnedDetails && data?.vehicleVisualRecord && (
          <ReturnedDetails
            data={data.vehicleVisualRecord}
            onClose={() => setShowReturnedDetails(false)}
          />
        )}

        {showMaintenanceDetails && data?.maintenanceVisualRecord && (
          <VehicleEndMaintenanceDetails
            data={data.maintenanceVisualRecord}
            onClose={() => setShowMaintenanceDetails(false)}
          />
        )}
      </div>

      {/* Modal de visualização de foto em tamanho maior */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setSelectedPhoto(null)}
                className="text-white text-sm px-3 py-1 rounded bg-black/40 hover:bg-black/60"
              >
                Fechar
              </button>
            </div>

            <img
              src={selectedPhoto}
              alt="Foto"
              className="w-full max-h-[80vh] object-contain rounded bg-black"
            />
          </div>
        </div>
      )}
    </Dashboard>
  );
}
